import {firstValueFrom} from "rxjs";
import {Model} from "./model";
import {SearchQueryOptions} from "./search-query-options";
import {ListResponse} from "./response-types";
import {MAX_PAGE_SIZE} from "../../shared/globals";

export const DEFAULT_MAX_PAGES = 20;

export interface PaginateAllResult<T> {
    rows: T[];
    /** True if the loop bailed because it hit `maxPages` before exhausting results. */
    hitCap: boolean;
}

export interface FetchAllPagesOptions {
    filters?: object[];
    pageSize?: number;
    maxPages?: number;
    /**
     * JSON:API sort key threaded into every page request. Defaults to `'id'`,
     * which guarantees a deterministic order across offset pages: PostgreSQL
     * row order without ORDER BY is unspecified, so without this two adjacent
     * pages can overlap or skip rows that move past a page boundary. Pass a
     * user-meaningful sort (e.g. `'name,id'`) when a visible order matters,
     * but always keep a unique tie-breaker like `id` at the end.
     */
    sort?: string;
    /** Polled before each page fetch; returning true exits the loop early. */
    isCancelled?: () => boolean;
}

/**
 * Page through `model.searchMany(...)` and collect every row matching `filters`.
 *
 * Caller decides what to do with `hitCap` (toast, throw, early-return) — the
 * helper does not call NotificationService directly.
 *
 * Dedupes by `id`: duplicate rows can leak across pages if the server inserts
 * rows mid-fetch and shifts offsets. JSON:API guarantees unique `id` per
 * resource, so dedup-by-id is safe for any `searchMany` consumer.
 */
export async function fetchAllPages<T extends {id: string}>(
    model: Model<T>,
    opts: FetchAllPagesOptions = {}
): Promise<PaginateAllResult<T>> {
    // Clamp to a positive integer so a stray 0 / negative / NaN can't make the
    // loop spin forever (a non-short page never terminates) or exit instantly
    // (maxPages <= 0); fall back to the defaults instead. Callers don't pass
    // these today, but the contract shouldn't carry those footguns.
    const clampPositiveInt = (value: number | undefined, fallback: number): number =>
        Number.isFinite(value) && (value as number) >= 1 ? Math.floor(value as number) : fallback;
    const pageSize = clampPositiveInt(opts.pageSize, MAX_PAGE_SIZE);
    const maxPages = clampPositiveInt(opts.maxPages, DEFAULT_MAX_PAGES);
    const sort = opts.sort ?? 'id';
    const collected: T[] = [];
    const seenIds = new Set<string>();
    let page = 1;

    while (true) {
        if (opts.isCancelled?.()) return {rows: collected, hitCap: false};

        const queryOpts = new SearchQueryOptions();
        queryOpts.page_size = pageSize;
        queryOpts.page_number = page;
        queryOpts.filters = opts.filters;
        queryOpts.sort = sort;

        const resp = await firstValueFrom(model.searchMany(queryOpts)) as ListResponse<T>;
        // A genuine empty page is the clean terminator. A falsy response or a
        // non-array `data`, though, is a malformed response mid-stream (e.g. an
        // error body that slipped through as a 200) — treating it as end-of-data
        // would silently truncate and report partial data as complete. The only
        // callers (download/pivot) need completeness and already wrap this in
        // try/catch + toast, so fail loudly rather than silently.
        if (!resp || !Array.isArray(resp.data)) {
            throw new Error('fetchAllPages: malformed response (missing data array) while paginating');
        }
        if (resp.data.length === 0) break;

        // Page ceiling. By the time `page` exceeds maxPages we've already collected
        // maxPages full pages and this fetch is the probe for a (maxPages+1)th.
        // Reaching here with a non-empty page means the result set genuinely exceeds
        // the ceiling, so report capped and return the first maxPages pages — do NOT
        // collect this probe page (partial data stays exactly at the ceiling). This
        // check sits AFTER the empty-page break above on purpose: a result of exactly
        // maxPages*pageSize rows is followed by an empty page that terminates the loop
        // as complete, where bailing on the cap before fetching the probe used to
        // misreport it as capped. Cost: one extra page request on the already-failing
        // over-ceiling path.
        if (page > maxPages) {
            return {rows: collected, hitCap: true};
        }

        // Dedupe-by-id but DO NOT treat a fully-deduplicated page as a terminator:
        // with offset pagination, rows inserted ahead of the cursor between
        // requests can make page N repeat ids from earlier pages while page N+1
        // still holds unseen rows. Bailing on the first all-duplicate page would
        // silently truncate the result.
        const fresh = resp.data.filter(d => !seenIds.has(d.id));
        fresh.forEach(d => seenIds.add(d.id));
        collected.push(...fresh);

        // Termination is driven purely by full-page detection (plus the maxPages
        // ceiling and isCancelled): a full page means there may be more, a short or
        // empty page is definitively the last. We deliberately do NOT use meta.count
        // as a terminator — count and the data query can diverge (separate
        // transactions / concurrent inserts), and an under-reported count would stop
        // the loop before a trailing full page is fetched. Cost: when the true total
        // is an exact multiple of pageSize, one extra request returns an empty page
        // and breaks above — negligible given the default pageSize (MAX_PAGE_SIZE).
        if (resp.data.length < pageSize) break;
        page++;
    }

    return {rows: collected, hitCap: false};
}
